// Fixed Topic list for the coach Syllabus Tracker — mirrors
// backend/models/syllabusEntry.js's SYLLABUS_TOPICS exactly.
export const SYLLABUS_TOPICS = [
  'Action Songs',
  'Scenes',
  'Computer apps',
  'Picture Book',
  'Saint Story',
  '2 Min Balgruha Stories',
  'Identity Formation',
];

// Singularized label used as the session-column prefix, e.g. "Scenes" -> "Scene 1".
const SESSION_LABEL = {
  'Action Songs': 'Action Song',
  Scenes: 'Scene',
  'Computer apps': 'Computer App',
  'Picture Book': 'Picture Book',
  'Saint Story': 'Saint Story',
  '2 Min Balgruha Stories': '2 Min Balgruha Story',
  'Identity Formation': 'Identity Formation',
};

export const getSessionLabel = (topic, sessionNumber) =>
  `${SESSION_LABEL[topic] || topic} ${sessionNumber}`;

// Singular column-header word for a topic, e.g. "Scenes" -> "Scene",
// used as the first table column header in the child detail view.
export const getSessionColumnHeader = (topic) => SESSION_LABEL[topic] || topic;

export const STATUS_OPTIONS = [
  { id: 'all', label: 'All Children' },
  { id: 'completed', label: 'Completed' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'not_started', label: 'Not Started' },
  { id: 'not_applicable', label: 'Not Applicable' },
];

// The most coins of a session whose chapter the admin gave no coins in
// Courses; one the admin did gets those, however many (the matrix's `maxScore`).
export const MAX_SESSION_SCORE = 30;

// The grade buttons for a session that can get at most `max` coins: the
// usual 0, 5 ... 30 up to it, and `max` itself. Above 30 (an admin limit of
// e.g. 94) a round step that keeps the row about as long (0, 20 ... 80, 94),
// leaving out a step that would sit right next to `max`.
export const gradeCoinOptions = (max = MAX_SESSION_SCORE) => {
  if (max <= MAX_SESSION_SCORE) {
    return [...new Set([...[0, 5, 10, 15, 20, 25, 30].filter((value) => value < max), max])];
  }
  const step = 5 * Math.ceil(max / MAX_SESSION_SCORE);
  const steps = [];
  for (let value = 0; max - value > step / 2; value += step) steps.push(value);
  return [...steps, max];
};

// The buttons of a coin rule (e.g. Expression, up to 20 coins): every number
// up to 10 coins, else as gradeCoinOptions.
export const ruleCoinOptions = (max) =>
  max <= 10 ? Array.from({ length: max + 1 }, (_, value) => value) : gradeCoinOptions(max);

// What a chapter's activity grade is for, from its content items' types
// (the matrix's `coinParts.activityTypes`): "PDF, Audio, Video & Game".
const ACTIVITY_TYPE_NAMES = {
  pdf: 'PDF', audio: 'Audio', video: 'Video', link: 'Game', image: 'Picture', text: 'Reading', task: 'Task',
};
export const activityName = (types = []) => {
  const names = [...new Set(types.map((type) => ACTIVITY_TYPE_NAMES[type] || type))];
  if (names.length === 0) return 'Activity';
  return names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
};

// A saved score shown split over a chapter's coin rules (`coinParts`):
// { activity, rules: { [label]: coins } }, with null for one not given.
// Given as one number before (no `ruleScores`), all of it is the activity's
// while it fits, else nothing is picked.
export const splitScore = (score, ruleScores, parts) => {
  const rules = Object.fromEntries((parts?.rules || []).map((rule) => [rule.label, null]));
  if (score === null || score === undefined) return { activity: null, rules };
  const given = (ruleScores || []).filter((entry) => entry.label in rules);
  if (given.length) {
    given.forEach((entry) => { rules[entry.label] = entry.score; });
    const total = given.reduce((sum, entry) => sum + (Number(entry.score) || 0), 0);
    return { activity: Math.max(0, score - total), rules };
  }
  return { activity: score <= (parts?.activity ?? score) ? score : null, rules };
};

// The score and `ruleScores` to save for a split: null score when nothing is
// picked; a rule not picked counts as 0 coins.
export const joinScore = (split) => {
  const ruleScores = Object.entries(split.rules)
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([label, value]) => ({ label, score: value }));
  if (split.activity === null && ruleScores.length === 0) return { score: null, ruleScores: [] };
  const score = (split.activity || 0) + ruleScores.reduce((sum, entry) => sum + entry.score, 0);
  return { score, ruleScores };
};

// 2 = green, 1 = yellow, 0 = red: the bands of the 0-30 scale (>= 26,
// 15-25, < 15), as the same share of a smaller `max`.
const scoreTier = (score, max = MAX_SESSION_SCORE) => {
  if (score * MAX_SESSION_SCORE >= 26 * max) return 2;
  if (score * MAX_SESSION_SCORE >= 15 * max) return 1;
  return 0;
};

// Score color bands (0-30 scale): green >= 26, yellow 15-25, red < 15.
export const getScoreBand = (score, isNotApplicable, max = MAX_SESSION_SCORE) => {
  if (isNotApplicable || score === null || score === undefined) return 'neutral';
  return ['danger', 'warning', 'success'][scoreTier(score, max)];
};

// Per-entry status label + tone for the child detail view's "Status" column
// — same score bands as getScoreBand, worded as the 4-state status a coach
// reads at a glance instead of just a chip color.
export const getEntryStatus = (score, isNotApplicable, max = MAX_SESSION_SCORE) => {
  if (isNotApplicable) return { id: 'not_applicable', label: 'N/A', tone: 'neutral' };
  if (score === null || score === undefined) return { id: 'not_started', label: 'Not Started', tone: 'neutral' };
  return [
    { id: 'started', label: 'Started', tone: 'danger' },
    { id: 'in_progress', label: 'In Progress', tone: 'warning' },
    { id: 'completed', label: 'Completed', tone: 'success' },
  ][scoreTier(score, max)];
};

// Generates { value: 'YYYY-MM', label: 'Month YYYY' } options for the last
// `pastMonths` months through the current month (newest first).
export const getMonthYearOptions = (pastMonths = 24) => {
  const options = [];
  const now = new Date();

  for (let i = 0; i <= pastMonths; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const value = `${year}-${String(month).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    options.push({ value, label, month, year });
  }

  return options;
};
